/**
 * Applying several saved filters at once.
 *
 * Combined with **AND**: selecting "US large cap" and "above the 50 day" means
 * both, which is what picking two filters off a list reads as. OR would make the
 * result *larger* with each one ticked, which nobody expects from adding a
 * filter.
 *
 * The combination is written into `filter` and the selection into
 * `activeFilterRefs`. That is deliberately two things: `filter` is what any
 * consumer evaluates — one node, whatever it came from — and the refs are only
 * provenance, so the picker can show what is ticked and a deleted filter
 * degrades to whatever remains rather than erroring.
 */

import type { SavedScreenerFilter, ScreenerConfig, ScreenerFilterNode } from "@qufolio/model";

/**
 * A condition whose rule the server withholds (`docs/plans/screen-links.md`).
 *
 * The flag, not a missing `filter`, is the test: an entry with neither is
 * malformed rather than sealed, and treating it as sealed would ask the server
 * to evaluate an id it has no rule for.
 */
export function isSealed(filter: SavedScreenerFilter): boolean {
  return filter.sealed === true;
}

/**
 * The library minus its sealed entries — what may be copied into another
 * screen. A sealed entry is a reference to its own screen's rule by id; copied
 * anywhere else it names a rule that screen does not have, and the only way to
 * make it work there is to hold the rule, which is what sealing forbids.
 */
export function withoutSealed(filters: readonly SavedScreenerFilter[]): SavedScreenerFilter[] {
  return filters.filter((f) => !isSealed(f));
}

/** Filters grouped for a tree: ★ favourites, named groups, then ungrouped. */
export interface FilterCategory {
  /** `""` for the ungrouped bucket, `FAVORITES` for the ★ section. */
  category: string;
  filters: SavedScreenerFilter[];
}

/**
 * The ★ section's key. Not a string a person can type as a category, so a
 * category named "Favourites" stays a category of its own.
 */
export const FAVORITES = "\u0000favorites";

/**
 * The picker's tree.
 *
 * **★ Favourites first, and a favourite stays in its own category too** — the
 * owner's mock (2026-10-02): the section is a shortcut to the ones used most,
 * not a place they move to. Then the categories `order` names, in that order
 * (`ScreenerConfig.savedFilterCategoryOrder`, set by whoever authors the
 * screen); then any it does not name, A–Z; then ungrouped. With no `order` and
 * no favourites this is exactly the alphabetical tree every screen had before.
 */
export function groupByCategory(
  filters: readonly SavedScreenerFilter[],
  order: readonly string[] = [],
): FilterCategory[] {
  const groups = new Map<string, SavedScreenerFilter[]>();
  for (const filter of filters) {
    const key = filter.category?.trim() ?? "";
    const list = groups.get(key);
    if (list) list.push(filter);
    else groups.set(key, [filter]);
  }
  for (const list of groups.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  const rank = new Map(order.map((name, i) => [name.trim(), i] as const));
  const tree = [...groups.entries()]
    .map(([category, list]) => ({ category, filters: list }))
    // Ungrouped last: named groups are the ones someone organised, and burying
    // them under a heap of loose filters undoes the organising.
    .sort((a, b) => {
      if (a.category === b.category) return 0;
      if (a.category === "") return 1;
      if (b.category === "") return -1;
      const ra = rank.get(a.category);
      const rb = rank.get(b.category);
      if (ra !== undefined || rb !== undefined) {
        if (ra === undefined) return 1;
        if (rb === undefined) return -1;
        return ra - rb;
      }
      return a.category.localeCompare(b.category);
    });

  const favorites = filters
    .filter((f) => f.favorite)
    .sort((a, b) => a.name.localeCompare(b.name));
  return favorites.length ? [{ category: FAVORITES, filters: favorites }, ...tree] : tree;
}

/** One group a condition comes from: `owner` `""` is this screen's own. */
export interface FilterSource {
  owner: string;
  /** Its categories, as `groupByCategory` orders them — no ★ section inside. */
  categories: FilterCategory[];
}

/**
 * The picker's tree once a screen links conditions from a group (owner,
 * 2026-11-01, `docs/linked-conditions-mock.html`): **Group, then Category**.
 *
 * ★ Favourites stays first and across every group. Then this screen's own
 * conditions, in the screen's category order; then one source per linked
 * group (`ownerOf`, e.g. `JP`), A–Z, each A–Z inside — the source screen's
 * order is not held here. `null` when nothing is linked: the caller keeps the
 * plain `groupByCategory` tree, so an unlinked screen looks exactly as before.
 */
export function groupBySource(
  filters: readonly SavedScreenerFilter[],
  order: readonly string[] | undefined,
  ownerOf: (filter: SavedScreenerFilter) => string | undefined,
): { favorites: SavedScreenerFilter[]; sources: FilterSource[] } | null {
  const owners = new Map<string, SavedScreenerFilter[]>();
  for (const filter of filters) {
    const owner = ownerOf(filter) ?? "";
    const list = owners.get(owner);
    if (list) list.push(filter);
    else owners.set(owner, [filter]);
  }
  if ([...owners.keys()].every((o) => o === "")) return null;
  const strip = (tree: FilterCategory[]) => tree.filter((g) => g.category !== FAVORITES);
  const sources: FilterSource[] = [...owners.entries()]
    .sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)))
    .map(([owner, list]) => ({
      owner,
      categories: strip(groupByCategory(list, owner === "" ? order : [])),
    }));
  const favorites = filters.filter((f) => f.favorite).sort((a, b) => a.name.localeCompare(b.name));
  return { favorites, sources };
}

/** Every category in use, for offering existing ones when saving. */
export function categoriesOf(filters: readonly SavedScreenerFilter[]): string[] {
  return [...new Set(filters.map((f) => f.category?.trim()).filter((c): c is string => !!c))].sort();
}

/**
 * The effective filter for a selection.
 *
 * One selected filter is used as-is rather than wrapped: an `all` group of one
 * evaluates identically but reads as a group in the editor, and the user picked
 * a filter, not a group.
 */
export function combineFilters(
  selected: readonly SavedScreenerFilter[],
): ScreenerFilterNode | undefined {
  // A sealed condition has no rule to combine, and nothing is invented for it:
  // it stays ticked in `activeFilterRefs`, and `withSealedConditions` ANDs its
  // server-computed flag in on the way to the grid (`sealed-conditions.ts`).
  // A linked one is the same: evaluated on the server by its link, even when
  // its source lets this reader see the rule (`screen-links.ts`) — combined in
  // here, the rule would be saved with the panel's filter.
  const nodes = selected
    .filter((f) => !isSealed(f) && !f.link)
    .map((f) => f.filter)
    .filter((node): node is ScreenerFilterNode => node != null);
  if (nodes.length === 0) return undefined;
  if (nodes.length === 1) return nodes[0]!;
  return { op: "all", children: [...nodes] };
}

/**
 * Which saved filters are currently applied.
 *
 * Reads both fields, because two things write them: the picker sets
 * `activeFilterRefs` for a multi-selection, while loading one filter in the
 * builder sets `activeFilterRef`. Without the fallback a filter applied from
 * the builder showed nothing ticked in the picker — which reads as the picker
 * having lost it, not as two fields meaning nearly the same thing.
 */
export function selectedFilterIds(config: ScreenerConfig): string[] {
  if (config.activeFilterRefs?.length) return [...config.activeFilterRefs];
  return config.activeFilterRef ? [config.activeFilterRef] : [];
}

/**
 * Apply a selection to a config.
 *
 * Selecting none clears the filter, which is the only reading of unticking
 * everything — leaving the last filter applied would make the picker lie.
 */
export function applySelection(
  config: ScreenerConfig,
  selectedIds: readonly string[],
): Pick<ScreenerConfig, "filter" | "activeFilterRefs" | "activeFilterRef"> {
  const saved = config.savedFilters ?? [];
  // Ordered by the library, not by click order, so the stored filter is stable
  // and two people ticking the same boxes get the same config.
  const selected = saved.filter((f) => selectedIds.includes(f.id));
  return {
    filter: combineFilters(selected),
    activeFilterRefs: selected.map((f) => f.id),
    // Kept in step when exactly one is picked, so the builder — which reads the
    // singular field — shows the same filter loaded. Cleared for a
    // multi-selection, where "loaded from one and possibly diverged" cannot be
    // true of several.
    activeFilterRef: selected.length === 1 ? selected[0]!.id : undefined,
  };
}

/** The library with one condition's ★ flipped. Off is absent, never `false`. */
export function toggleFavorite(
  filters: readonly SavedScreenerFilter[],
  id: string,
): SavedScreenerFilter[] {
  return filters.map((f) => {
    if (f.id !== id) return f;
    if (f.favorite) {
      const { favorite: _dropped, ...rest } = f;
      return rest;
    }
    return { ...f, favorite: true };
  });
}

/**
 * The category order after moving `category` one place up (-1) or down (+1).
 *
 * Returned **whole** — every named category in the order the picker shows them
 * now, with the one moved — so the stored order stops depending on the
 * alphabetical fallback the moment anybody arranges anything. ★ Favourites and
 * Ungrouped are not categories here: one is always first, the other last.
 */
export function moveCategory(
  filters: readonly SavedScreenerFilter[],
  order: readonly string[] | undefined,
  category: string,
  delta: -1 | 1,
): string[] {
  const shown = groupByCategory(filters, order)
    .map((g) => g.category)
    .filter((c) => c !== FAVORITES && c !== "");
  const at = shown.indexOf(category);
  const to = at + delta;
  if (at < 0 || to < 0 || to >= shown.length) return shown;
  const next = [...shown];
  [next[at], next[to]] = [next[to]!, next[at]!];
  return next;
}
